import React, { useId, useState } from 'react';

import { Option } from './SelectorInputField';
import Label from './Label';
import Error, { type ErrorMessage } from './Error';
import { fieldWidths } from './Form';
import clsx from 'clsx';
import { Mercury } from '@repo/types/Models';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../ui/command';
import { cn } from '../lib/utils';
import { CheckIcon } from 'lucide-react';

type Value =
  | {
      label: string | null;
      value: string | null;
    }
  | undefined;

export interface PaginatedSelectorInputFieldProps<T> {
  label: string;
  description?: string;
  name: string;
  id?: string;
  additionalContainerClasses?: string;
  onChange: (v: T) => void;
  value: Value;
  defaultValue?: Value;
  error?: ErrorMessage;
  pageSize: number;
  setQueryVars: (vars: Mercury.GQL.QueryVariables) => void;
  loading?: boolean;
  options: Option[];
  pageInfo?: Mercury.GQL.PageInfo;
  width?: keyof typeof fieldWidths;
}

export const PaginatedSelectorInputField = function PaginatedSelectorInputField<
  T extends string | null,
>({
  label,
  description,
  name,
  id,
  additionalContainerClasses,
  onChange,
  value,
  defaultValue,
  options,
  error,
  loading,
  pageSize,
  pageInfo,
  width = 'full',
  setQueryVars,
}: PaginatedSelectorInputFieldProps<T>) {
  const [open, setOpen] = useState(false);
  const [currentQuery, setCurrentQuery] = useState<string>('');

  const handleSearch = (query: string) => {
    setCurrentQuery(query);
    setQueryVars({
      first: pageSize,
      query: query,
    });
  };

  const handleLoadMore = () => {
    setQueryVars({
      first: pageSize,
      after: pageInfo?.endCursor,
      ...(currentQuery ? { query: currentQuery } : {}),
    });
  };

  const handleLoadPrevious = () => {
    setQueryVars({
      last: pageSize,
      before: pageInfo?.startCursor,
      ...(currentQuery ? { query: currentQuery } : {}),
    });
  };

  const handleSelect = (option: Option) => {
    onChange(option as unknown as T);
    setOpen(false);
  };

  const containerClasses = clsx(
    'relative gap-2 flex flex-col w-full h-full',
    additionalContainerClasses
  );
  const defaultId = useId();
  id = id ?? defaultId;

  // Determine the display label - controlled via React state, not internal cache
  const displayLabel = value?.label ?? defaultValue?.label ?? null;
  const currentValue = value?.value ?? defaultValue?.value ?? null;

  return (
    <div id={id} className={clsx(containerClasses, fieldWidths[width])}>
      <Label htmlFor={`${id}::combo`} description={description}>
        {label}
      </Label>

      <input type="hidden" name={name} value={currentValue ?? ''} />

      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={`${id}::combo`}
            role="combobox"
            aria-expanded={open}
            className="flex justify-between items-center px-2 w-full text-left rounded-md border appearance-none dark:text-blue-100 text-grey-1000 py-[8px] bg-grey-100 border-grey-500 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none"
          >
            <span className="truncate">{displayLabel ?? 'Select...'}</span>
            <span className="pointer-events-none dark:text-blue-100 text-grey-1000 material-symbols-outlined">
              expand_more
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 min-w-[250px] max-w-[300px] w-[--radix-popover-trigger-width] bg-grey-100 border-grey-400 dark:bg-blue-800 dark:border-grey-600"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search..."
              value={currentQuery}
              onValueChange={handleSearch}
              className="dark:text-blue-100 text-grey-1000"
            />
            <div className="flex gap-2 justify-end px-2 py-1 border-b border-grey-400 dark:border-grey-600">
              <button
                type="button"
                onClick={handleLoadPrevious}
                disabled={!pageInfo?.hasPreviousPage}
                className="disabled:hidden"
              >
                <span className="text-2xl pointer-events-none dark:text-blue-100 text-grey-1000 material-symbols-outlined">
                  arrow_circle_left
                </span>
              </button>

              <button
                type="button"
                onClick={handleLoadMore}
                disabled={!pageInfo?.hasNextPage}
                className="disabled:hidden"
              >
                <span className="text-2xl pointer-events-none dark:text-blue-100 text-grey-1000 material-symbols-outlined">
                  arrow_circle_right
                </span>
              </button>
            </div>
            <CommandList>
              <CommandGroup>
                {loading
                  ? Array.from({ length: pageSize }, (_, i) => (
                      <div key={i} className="w-full px-2 py-1.5">
                        <div className="flex items-center w-full h-full animate-pulse">
                          <div className="w-full h-6 rounded bg-grey-300 dark:bg-blue-700"></div>
                        </div>
                      </div>
                    ))
                  : options.length > 0
                    ? options.map((option) => (
                        <CommandItem
                          key={option.value.toString()}
                          value={option.value.toString()}
                          onSelect={() => handleSelect(option)}
                          className="cursor-pointer whitespace-normal hover:bg-blue-200 dark:hover:bg-blue-700 dark:text-blue-100 text-grey-1000"
                        >
                          <CheckIcon
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              currentValue === option.value ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div
                            className="flex flex-col min-w-0"
                            style={{ overflowWrap: 'anywhere' }}
                          >
                            <span className="text-wrap">{option.label}</span>
                            {option.description && (
                              <span className="text-xs text-wrap text-grey-500 dark:text-grey-400">
                                {option.description}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))
                    : null}
              </CommandGroup>
              {!loading && options.length === 0 && <CommandEmpty>No results found</CommandEmpty>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Error error={error} />
    </div>
  );
};
